import {
  FlowStep,
  IntentSelectionInput,
  UpsertFlowStepInput,
} from '@varo/graphql-schema-types';
import { usePathname } from 'expo-router';
import OnboardingSelection from 'features/onboarding/components/OnboardingSelection/OnboardingSelection';
import useGetCurrentFlowStep from 'features/onboarding/hooks/useGetCurrentFlowStep';
import useUpsertFlowStep from 'features/onboarding/hooks/useUpsertFlowStep';
import { getIntentOptions } from 'features/onboarding/utils/getIntentOptions';
import { mapToERoute } from 'features/onboarding/utils/mapToERoute';
import { redirectUserToTargetRoute } from 'features/onboarding/utils/redirectUserToTargetRoute';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AbortError } from 'shared/hooks/useCancellableMutation';

const IntentScreen = () => {
  const pathname = usePathname();
  const { t: translatedText } = useTranslation('onboarding', {
    keyPrefix: 'intent',
  });

  const { flowStepId, isCurrentFlowStepLoading, currentFlowStepData } =
    useGetCurrentFlowStep();

  const { isUpsertFlowStepLoading, submitUpsertFlowStep } = useUpsertFlowStep();

  const handleContinueButton = async (
    id: string | undefined,
    didSkip?: boolean,
  ) => {
    const isSkipped = Boolean(didSkip);
    const input: UpsertFlowStepInput = {
      id: flowStepId,
      intentBasedOnboarding: {
        selection: id ? (id as IntentSelectionInput) : undefined,
        didSkip: isSkipped,
      },
    };

    try {
      const result = await submitUpsertFlowStep({
        input,
      });
      if (!result.error) {
        redirectUserToTargetRoute(result.data?.upsertFlowStep as FlowStep);
      }
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }
    }
  };

  useEffect(() => {
    // Skip if there's no step yet, or if navigating would redirect back to
    // the current screen (avoids an infinite loop).
    if (
      !currentFlowStepData?.currentStep ||
      mapToERoute(currentFlowStepData.currentStep) === pathname
    ) {
      return;
    }

    redirectUserToTargetRoute(currentFlowStepData);
  }, [currentFlowStepData, pathname]);

  return (
    <OnboardingSelection
      optionsData={getIntentOptions(translatedText)}
      headlineText={translatedText('headline')}
      onContinueButton={handleContinueButton}
      isLoading={isCurrentFlowStepLoading || isUpsertFlowStepLoading}
      skipButtonAnalyticsId="intentMethodSkipped"
      continueButtonAnalyticsId="intentMethodOptionSubmitted"
    />
  );
};

export default IntentScreen;
